import Keycloak from 'keycloak-js';

const SESSION_EXPIRATION_MESSAGE =
  'Sua sessao terminou por seguranca. Clique em OK para entrar novamente.';
const SESSION_STORAGE_KEY = 'logistica.keycloak.session';

type SessionExpirationListener = (message: string) => void;
type StoredSession = {
  idToken?: string;
  timeSkew?: number;
  token: string;
};

const keycloakInstance = Keycloak({
  realm: 'lar',
  url: 'https://sso.lar.ind.br/auth/',
  clientId: process.env.REACT_APP_KEYCLOAL_CLIENT_ID as string
});

const sessionExpirationListeners = new Set<SessionExpirationListener>();
let sessionExpirationHandled = false;

const decodeTokenPayload = (token: string): any => {
  const payload = token.split('.')[1];
  const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
  const paddedPayload = normalizedPayload.padEnd(
    normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
    '='
  );

  return JSON.parse(atob(paddedPayload));
};

const getCurrentAppUrl = (): string => `${window.location.origin}${window.location.pathname}`;

const getLogoutUrl = (redirectUri?: string): string => {
  const logoutUrl = new URL(
    `realms/${encodeURIComponent('lar')}/protocol/openid-connect/logout`,
    'https://sso.lar.ind.br/auth/'
  );

  logoutUrl.searchParams.set('post_logout_redirect_uri', redirectUri || getCurrentAppUrl());

  if (keycloakInstance.idToken) {
    logoutUrl.searchParams.set('id_token_hint', keycloakInstance.idToken);
  } else {
    logoutUrl.searchParams.set('client_id', process.env.REACT_APP_KEYCLOAL_CLIENT_ID as string);
  }

  return logoutUrl.toString();
};

const hasAuthenticationCallback = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);

  return (
    (searchParams.has('code') && searchParams.has('state')) ||
    searchParams.has('session_state') ||
    searchParams.has('error')
  );
};

const clearStoredSession = (): void => {
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
};

const getStoredSession = (): StoredSession | null => {
  const storedValue = sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (!storedValue) {
    return null;
  }

  try {
    const parsedSession = JSON.parse(storedValue) as StoredSession;
    const expirationDate = parsedSession.token
      ? new Date(decodeTokenPayload(parsedSession.token).exp * 1000)
      : null;

    if (!expirationDate || expirationDate.getTime() <= Date.now()) {
      clearStoredSession();
      return null;
    }

    return parsedSession;
  } catch {
    clearStoredSession();
    return null;
  }
};

const persistCurrentSession = (): void => {
  if (!keycloakInstance.token) {
    clearStoredSession();
    return;
  }

  sessionStorage.setItem(
    SESSION_STORAGE_KEY,
    JSON.stringify({
      token: keycloakInstance.token,
      idToken: keycloakInstance.idToken,
      timeSkew: keycloakInstance.timeSkew
    })
  );
};

const applyStoredSession = (storedSession: StoredSession): void => {
  const tokenParsed = decodeTokenPayload(storedSession.token);
  const idTokenParsed = storedSession.idToken
    ? decodeTokenPayload(storedSession.idToken)
    : undefined;

  keycloakInstance.token = storedSession.token;
  keycloakInstance.tokenParsed = tokenParsed;
  keycloakInstance.idToken = storedSession.idToken;
  keycloakInstance.idTokenParsed = idTokenParsed;
  keycloakInstance.timeSkew = storedSession.timeSkew;
  keycloakInstance.authenticated = true;
  keycloakInstance.subject = tokenParsed.sub;
  keycloakInstance.sessionId = tokenParsed.session_state;
  keycloakInstance.realmAccess = tokenParsed.realm_access;
  keycloakInstance.resourceAccess = tokenParsed.resource_access;
};

const logTokenExpiration = (): void => {
  const expiration = (keycloakInstance.tokenParsed as any)?.exp;

  if (!expiration) {
    return;
  }

  const expirationDate = new Date(expiration * 1000);
};

const getTokenExpirationDate = (): Date | null => {
  const expiration = (keycloakInstance.tokenParsed as any)?.exp;

  if (!expiration) {
    return null;
  }

  return new Date(expiration * 1000);
};

const isLoggedIn = (): boolean => !!keycloakInstance.token;

const logRemainingSessionTime = (): void => {
  const expirationDate = getTokenExpirationDate();

  if (!expirationDate || !isLoggedIn()) {
    return;
  }

  const remainingMs = Math.max(expirationDate.getTime() - Date.now(), 0);
  const remainingSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const formattedRemainingTime = `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;

  console.info(`Tempo restante antes do logoff automatico: ${formattedRemainingTime}`);
};

const notifySessionExpired = (message = SESSION_EXPIRATION_MESSAGE): void => {
  if (sessionExpirationHandled) {
    return;
  }

  sessionExpirationHandled = true;
  clearStoredSession();
  keycloakInstance.clearToken();
  sessionExpirationListeners.forEach((listener) => listener(message));
};

const doLogin = async (options?: any): Promise<void> => {
  const loginUrl = await keycloakInstance.createLoginUrl({
    prompt: 'login',
    redirectUri: window.location.href,
    ...options
  });

  window.location.assign(loginUrl);
};

const getInitOptions = (extraOptions?: any): any => ({
  silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
  checkLoginIframe: false,
  pkceMethod: 'S256',
  redirectUri: window.location.href,
  ...extraOptions
});

const initKeycloak = (onAuthenticatedCallback: any): any => {
  const storedSession = getStoredSession();

  keycloakInstance.onTokenExpired = () => {
    notifySessionExpired();
  };

  if (storedSession) {
    sessionExpirationHandled = false;
    applyStoredSession(storedSession);
    onAuthenticatedCallback();
    return;
  }

  if (!hasAuthenticationCallback()) {
    clearStoredSession();

    keycloakInstance
      .init(
        getInitOptions({
          onLoad: 'login-required'
        })
      )
      .then((authenticated) => {
        if (!authenticated) {
          return;
        }

        sessionExpirationHandled = false;
        persistCurrentSession();
        logTokenExpiration();
        onAuthenticatedCallback();
      });

    return;
  }

  keycloakInstance.init(getInitOptions()).then((authenticated) => {
    if (!authenticated) {
      clearStoredSession();
      doLogin();
      return;
    }

    sessionExpirationHandled = false;
    persistCurrentSession();
    logTokenExpiration();

    onAuthenticatedCallback();
  });
};

const doLogout = async (options?: any): Promise<void> => {
  clearStoredSession();

  const targetRedirectUri = options?.redirectUri || getCurrentAppUrl();

  window.location.assign(getLogoutUrl(targetRedirectUri));

  return Promise.resolve();
};

const getToken = (): string | undefined => keycloakInstance.token;

const getPreferredUsername = (): string => {
  if (keycloakInstance && keycloakInstance.tokenParsed) {
    return (keycloakInstance.tokenParsed as any).preferred_username;
  }
  return 'Nao autenticado';
};

const getRoles = (resourceAccessName: string): string[] => {
  try {
    if (keycloakInstance && keycloakInstance.tokenParsed) {
      return (keycloakInstance.tokenParsed as any).resource_access[resourceAccessName].roles;
    }
    return [];
  } catch {
    return [];
  }
};

const getUsername = (): string => {
  if (keycloakInstance && keycloakInstance.tokenParsed) {
    return (keycloakInstance.tokenParsed as any).name;
  }
  return 'Nao autenticado';
};

const getUserEmail = (): string => {
  if (keycloakInstance && keycloakInstance.tokenParsed) {
    return (keycloakInstance.tokenParsed as any).email;
  }
  return 'Nao autenticado';
};

const hasRealmRole = (roles: string[]): boolean =>
  roles.some((role) => keycloakInstance.hasRealmRole(role));

const hasResourceRole = (roles: string[]): boolean =>
  roles.some((role) => keycloakInstance.hasResourceRole(role));

export function getUserAttribute<T>(attrKey: string): T {
  if (keycloakInstance && keycloakInstance.tokenParsed) {
    return (keycloakInstance.tokenParsed as any)[attrKey] as T;
  }

  throw new Error(`Atributo ${attrKey} nao encontrado`);
}

const getRemainingSessionMs = (): number => {
  const expirationDate = getTokenExpirationDate();

  if (!expirationDate) {
    return 0;
  }

  return Math.max(expirationDate.getTime() - Date.now(), 0);
};

const subscribeToSessionExpiration = (listener: SessionExpirationListener): (() => void) => {
  sessionExpirationListeners.add(listener);

  return () => {
    sessionExpirationListeners.delete(listener);
  };
};

const handleAuthenticationFailure = (message = SESSION_EXPIRATION_MESSAGE): void => {
  notifySessionExpired(message);
};

const redirectToLoginAfterExpiration = (): Promise<void> => doLogout();

const isSessionExpirationHandled = (): boolean => sessionExpirationHandled;

const KeycloakService = {
  initKeycloak,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  getPreferredUsername,
  getUsername,
  getUserEmail,
  hasRealmRole,
  hasResourceRole,
  getRoles,
  getUserAttribute,
  getTokenExpirationDate,
  getRemainingSessionMs,
  logRemainingSessionTime,
  subscribeToSessionExpiration,
  handleAuthenticationFailure,
  redirectToLoginAfterExpiration,
  isSessionExpirationHandled,
  SESSION_EXPIRATION_MESSAGE
};

export default KeycloakService;
